<spark-sidebar
	[config]="sidebarConfig"
	(disableLinkClicked)="openModal($event)"
	(menuCollapseChange)="onMenuCollapseChange($event)"
>
	<div class="sidebar-profile">
		<!-- User avatar -->
		<div
			class="profile-img-cont"
			#profileAvatarContainer
		>
			<button
				type="button"
				class="edit-avatar"
				aria-label="Edit profile picture"
				(click)="onEditAvatarClick()"
			>
				<ng-fa-icon
					class="d-inline-flex edit-icon-text-size"
					icon="pen"
				></ng-fa-icon>
			</button>
			<img
				[src]="userInfo?.avatarImage || ('assets/shared/profile/profile' + (userInfo?.avatar || 6) + '.jpg')"
				class="img-fluid rounded-circle"
				alt="Flying User"
			/>
		</div>

		<!-- User name -->
		<div class="sidebar-profile-copy">
			<span>Signed in as</span>
			<strong class="dark text-capitalize text-truncate">
				{{ userInfo?.firstname }} {{ userInfo?.lastname }}
			</strong>
		</div>
	</div>

	<!-- Community links -->
	<div
		*ngIf="appearancePreferenceService.sidebarCommunityEnabled$ | async"
		@appearancePreferenceVisibility
		class="sidebar-community"
		aria-label="Community links"
	>
		<div class="sidebar-favorites-heading">
			<span>Community</span>
			<div class="sidebar-favorites-heading-actions">
				<a
					class="sidebar-favorites-settings"
					[routerLink]="['/settings/appearance']"
					[queryParams]="{ highlight: 'sidebar-community' }"
					aria-label="Open sidebar community settings"
					title="Sidebar community settings"
				>
					<ng-fa-icon icon="gear"></ng-fa-icon>
				</a>
			</div>
		</div>
		<div class="sidebar-community-links">
			<a
				*ngFor="let link of COMMUNITY_LINKS"
				class="sidebar-community-link"
				[ngClass]="'sidebar-community-link--' + link.platform"
				[href]="link.url"
				target="_blank"
				rel="noreferrer noopener"
				[attr.aria-label]="link.label"
				[title]="link.label"
			>
				<ng-fa-icon
					[icon]="link.icon"
					iconStyle="brands"
				></ng-fa-icon>
				<span *ngIf="link.badge">{{ link.badge }}</span>
			</a>
		</div>
	</div>

	<!-- Favorite shortcuts -->
	<div
		*ngIf="appearancePreferenceService.sidebarFavoritesEnabled$ | async"
		@appearancePreferenceVisibility
		class="sidebar-favorites"
		aria-label="Favorite shortcuts"
	>
		<div class="sidebar-favorites-heading">
			<span>Favorites</span>
			<div class="sidebar-favorites-heading-actions">
				<small>{{ favoriteCount }}/4</small>
				<a
					class="sidebar-favorites-settings"
					[routerLink]="['/settings/appearance']"
					[queryParams]="{ highlight: 'sidebar-favorites' }"
					aria-label="Open sidebar favorites settings"
					title="Sidebar favorites settings"
				>
					<ng-fa-icon icon="gear"></ng-fa-icon>
				</a>
			</div>
		</div>
		<div class="sidebar-favorite-slots">
			<ng-container *ngFor="let slotIndex of FAVORITE_SLOT_INDEXES">
				<div class="sidebar-favorite-slot-shell">
					<ng-container *ngIf="favoriteSlots[slotIndex] as favorite; else emptyFavoriteSlot">
						<button
							type="button"
							class="sidebar-favorite-slot"
							[ngClass]="'sidebar-favorite-slot--' + favorite.app"
							[attr.aria-label]="'Open ' + favorite.label + ' in Flying ' + favorite.appLabel"
							[title]="favorite.label + ' · Flying ' + favorite.appLabel"
							(click)="navigateToFavorite(favorite)"
						>
							<ng-fa-icon
								[icon]="favorite.icon.name"
								[iconStyle]="favorite.icon.style"
							></ng-fa-icon>
						</button>
						<button
							type="button"
							class="sidebar-favorite-remove"
							[attr.aria-label]="'Remove ' + favorite.label + ' from favorites'"
							[title]="'Remove ' + favorite.label"
							(click)="removeFavorite($event, slotIndex)"
						>
							<ng-fa-icon icon="xmark"></ng-fa-icon>
						</button>
					</ng-container>

					<ng-template #emptyFavoriteSlot>
						<button
							type="button"
							class="sidebar-favorite-slot sidebar-favorite-slot--empty"
							[attr.aria-label]="'Add favorite shortcut ' + (slotIndex + 1)"
							[title]="'Add favorite shortcut ' + (slotIndex + 1)"
							(click)="openFavoritePicker(slotIndex)"
						>
							<ng-fa-icon icon="plus"></ng-fa-icon>
						</button>
					</ng-template>
				</div>
			</ng-container>
		</div>
	</div>
</spark-sidebar>

<!-- Favorite shortcut picker -->
<ng-template
	#favoritePicker
	let-modal
>
	<div class="modal-header pod-ai-standard-modal-header">
		<div>
			<span class="sidebar-favorite-picker-eyebrow">Favorite {{ favoritePickerSlot + 1 }}</span>
			<h4 class="modal-title no-selection">Choose a shortcut</h4>
		</div>
		<button
			type="button"
			class="modal-dismiss-control"
			aria-label="Close favorite picker"
			(click)="modal.dismiss()"
		>
			&times;
		</button>
	</div>

	<div class="modal-body pod-ai-standard-modal-body sidebar-favorite-picker-body">
		<p>Choose any available page from Flying Research or Flying Upload.</p>

		<label class="sidebar-favorite-search ui-compound-control">
			<ng-fa-icon icon="magnifying-glass"></ng-fa-icon>
			<input
				type="search"
				placeholder="Search pages"
				aria-label="Search favorite pages"
				[(ngModel)]="favoriteSearch"
			/>
		</label>

		<div
			class="sidebar-favorite-filters"
			aria-label="Filter pages by application"
		>
			<button
				type="button"
				[class.active]="favoriteProductFilter === 'all'"
				(click)="favoriteProductFilter = 'all'"
			>
				All
			</button>
			<button
				type="button"
				class="research"
				[class.active]="favoriteProductFilter === EnumWebAppTool.Research"
				(click)="favoriteProductFilter = EnumWebAppTool.Research"
			>
				Research
			</button>
			<button
				type="button"
				class="upload"
				[class.active]="favoriteProductFilter === EnumWebAppTool.Upload"
				(click)="favoriteProductFilter = EnumWebAppTool.Upload"
			>
				Upload
			</button>
		</div>

		<div class="sidebar-favorite-options">
			<button
				*ngFor="let option of filteredFavoriteOptions"
				type="button"
				class="sidebar-favorite-option"
				[ngClass]="'sidebar-favorite-option--' + option.app"
				[disabled]="isFavoriteSelected(option)"
				(click)="selectFavorite(option); modal.close()"
			>
				<span class="sidebar-favorite-option-icon">
					<ng-fa-icon
						[icon]="option.icon.name"
						[iconStyle]="option.icon.style"
					></ng-fa-icon>
				</span>
				<span class="sidebar-favorite-option-copy">
					<strong>{{ option.label }}</strong>
					<small>{{ option.appLabel }} · {{ option.groupLabel }}</small>
				</span>
			</button>

			<div
				*ngIf="!filteredFavoriteOptions.length"
				class="sidebar-favorite-empty"
			>
				<ng-fa-icon icon="magnifying-glass"></ng-fa-icon>
				<strong>No matching pages</strong>
				<span>Try another search or application filter.</span>
			</div>
		</div>
	</div>

	<div class="modal-footer pod-ai-standard-modal-footer justify-content-center">
		<button
			type="button"
			class="btn btn-primary text-white"
			(click)="modal.dismiss()"
		>
			Close
		</button>
	</div>
</ng-template>

<!-- Module Info Popup -->
<ng-template
	#content
	let-modal
>
	<!-- Modal dismiss button -->
	<span
		class="position-absolute btn btn-default text-white close-button modal-dismiss-control"
		aria-label="Close"
		(click)="modal.dismiss()"
	>
		&times;
	</span>

	<ng-youtube-video-preview
		[youtubeVideoData]="{ title: this.moduleInfo.title, videos: this.moduleInfo.video }"
		[isModalPreview]="true"
		[videoContainerWidth]="800"
	>
		<!-- Message -->
		<div
			class="px-3 py-2"
			[class.pt-4]="!this.moduleInfo.video"
		>
			<p class="text-center h5">{{ moduleInfo.message }}</p>
		</div>

		<!-- Coming soon -->
		<div
			class="d-flex flex-column justify-content-center align-items-center coming-soon-container-height"
			*ngIf="!this.moduleInfo.video"
		>
			<h5 class="mb-3">Tutorial Video Coming Soon...</h5>
			<img
				[src]="MASCOT_IMG_LINKS.CHRISTMAS_CANDY"
				width="200"
			/>
		</div>

		<!-- Go to store -->
		<div class="modal-footer d-flex justify-content-center">
			<button
				class="btn btn-primary"
				routerLink="/store"
				(click)="modal.dismiss()"
			>
				Go to Store
			</button>
		</div>
	</ng-youtube-video-preview>
</ng-template>
